import { Button, Menu, MenuItem, OutlinedInput, Popover, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { titleStr } from "../../lib/helper";
import { FormInputProps } from "../../models/form.model";
import { ArrowDropDown } from "@mui/icons-material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useConfig } from "../../containers/ConfigContext";
import { FilterModel } from "../../models/report.model";

type Value = FilterModel["period"];

const viewsMap = {
  daily: ["year", "month", "day"],
  monthly: ["year", "month"],
  yearly: ["year"],
} as const

export default function FormPeriod(props: FormInputProps) {
  const { name, control, label, rules, setError } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [calendarEl, setCalendarEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { localized } = useConfig();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value: _value }, fieldState: { error } }) => {
        const value = _value as Value;
        const periodType = value?.type || "monthly";
        const range = (value?.range || []).filter(Boolean).map((v) => moment(v));
        const openTo = periodType === "monthly" ? "month" : periodType === "yearly" ? "year" : "day";
        const valueDisplay = periodType === "monthly" ? "YY MMM" : periodType === "yearly" ? "YYYY" : "MMM DD";

        const handleCalendarChange = (changedValue: string, startOfEnd: 'start' | 'end') => {
          const range = value?.range || [];
          if (changedValue) {
            range[startOfEnd === 'start' ? 0 : 1] = moment.utc(changedValue)[startOfEnd === 'start' ? 'startOf' : 'endOf']("day").toISOString();
          }
          const period: Value = {
            type: periodType,
            range,
          };

          onChange(period);
        }

        return (
          <>
            <OutlinedInput
              error={!!error}
              placeholder={label}
              onClick={(e) => {
                if ((e.target as HTMLElement).tagName === "INPUT") {
                  setCalendarEl(e.currentTarget);
                }
              }}
              value={range.map((a) => a.format(valueDisplay)).join(" - ")}
              fullWidth
              endAdornment={
                <>
                  <Tooltip title={localized(titleStr(value?.type || "monthly"))}>
                    <Button
                      endIcon={<ArrowDropDown />}
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {titleStr(value?.type || "monthly").charAt(0)}
                    </Button>
                  </Tooltip>

                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    {["Daily", "Monthly", "Yearly"].map((option) => (
                      <MenuItem
                        key={option}
                        onClick={(e) => {
                          onChange({ type: option.toLowerCase(), range: [] });
                          e.stopPropagation();
                        }}
                      >
                        {localized(option)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              }
            />

            {
              <Popover
                open={Boolean(calendarEl)}
                anchorEl={calendarEl}
                onClose={() => setCalendarEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Stack direction={smallScreen ? "column" : "row"} spacing={2}>
                    <div>
                      <div className="padding-big" style={{ paddingBottom: 0 }}>
                        {localized("Start")}
                      </div>
                      <DateCalendar
                        views={viewsMap[periodType]}
                        disableFuture
                        value={moment(range[0])}
                        openTo={openTo}
                        onChange={(v) => {
                          handleCalendarChange(v, 'start');
                        }}
                      />
                    </div>
                    <div>
                      <div className="padding-big" style={{ paddingBottom: 0 }}>
                        {localized("End")}
                      </div>
                      <DateCalendar
                        views={viewsMap[periodType]}
                        disableFuture
                        value={range[1]}
                        openTo={openTo}
                        onChange={(v) => {
                          handleCalendarChange(v, 'end');
                        }}
                      />
                    </div>
                  </Stack>
                </LocalizationProvider>
              </Popover>
            }
          </>
        );
      }}
    />
  );
}
